import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { verifyotp, sendotp, getProfile } from "../api/user";
import { toast } from "react-toastify";
import PageContainer from "../components/pages/PageContainer";
import useAuthScreenBackground from "../hooks/useAuthScreenBackground";
import {
  clearPendingSignup,
  getPendingSignupEmail,
  getVerificationToken,
  setVerificationToken,
} from "../utils/pendingSignup";

// The code length is set by the backend (OTP_LENGHT). Kept in one place here so
// the copy, the maxLength and the "enter the code" message cannot drift apart.
const CODE_LENGTH = 8;
const CODE_MINUTES = 10;

window.dataLayer = window.dataLayer || [];

// Maps the backend's existing resend-code responses (lang/en/message.php) to
// display copy. OTP_RESEND carries its countdown as "1 minutes 30 seconds".
function resendCodeMessage(message, fallback = message) {
  if (message === "Please wait a moment before requesting another code.") {
    return "Wait before requesting another code.";
  }
  const wait =
    typeof message === "string" &&
    message.match(/^Please try again after (.+) to resend the verification code\.$/);
  if (wait) {
    const part = (unit) => Number(wait[1].match(new RegExp(`(\\d+) ${unit}`))?.[1] || 0);
    const seconds = part("hours") * 3600 + part("minutes") * 60 + part("seconds");
    return `Try again in ${seconds}s.`;
  }
  if (message === "We could not read the email for this request. Please start again.") {
    return "We could not find the email for this request. Start again.";
  }
  if (message === "We could not find an account for that email address.") {
    return "We could not find an account for that email.";
  }
  return fallback;
}

const MISSING_CODE_MESSAGE = `Enter the ${CODE_LENGTH} digit verification code.`;

function verificationFailureReason(message) {
  switch (message) {
    case "That code is incorrect. Check the code and try again.":
      return "invalid_code";
    case "That code has expired. Request a new code.":
      return "expired_code";
    case "You have exceeded the maximum number of attempts to verify.":
      return "attempt_limit";
    default:
      return "system_error";
  }
}

const VerificationPage = () => {
  // Cream all the way to the screen edges - see the hook for why the wrapper
  // div alone could not reach behind the status bar and home indicator.
  useAuthScreenBackground();
  const navigate = useNavigate();

  const [verificationCode, setVerificationCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [codeError, setCodeError] = useState(null);
  // Read once on mount. Written by register, by sign-in on an unverified
  // account, and by Google sign-in - so the screen always knows who it is
  // verifying without asking the user to type the address a second time.
  const [pendingEmail, setPendingEmail] = useState(() => getPendingSignupEmail());
  // Same breakpoint check the account-creation screens use, so only one copy of
  // the form is ever in the DOM (duplicate input ids otherwise).
  const [isMobileView] = useState(window.innerWidth < 768);

  const fetchProfile = async () => {
    try {
      const response = await getProfile();

      if (response?.data?.data) {
        localStorage.setItem("img", response?.data?.data?.image);
        localStorage.setItem("userInfo", JSON.stringify(response.data.data));
        const myCustomEvent = new CustomEvent("profileEvent", {
          detail: {
            profileImg: response?.data?.data?.image,
            profileData: response?.data?.data,
          },
        });
        window.dispatchEvent(myCustomEvent);
      }
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const handleVerification = async (e) => {
    if (e) e.preventDefault();

    const code = verificationCode.trim();
    if (code.length !== CODE_LENGTH) {
      setCodeError(MISSING_CODE_MESSAGE);
      return;
    }

    setCodeError(null);
    setIsVerifying(true);

    // Both handles go out. `token` is the one send-otp issued in THIS browser
    // and is often absent (verifying from a second browser, or after a
    // rate-limited send); the pending email covers those cases, and the API
    // accepts either.
    const body = {
      otp: code,
      token: getVerificationToken(),
      email: pendingEmail,
    };

    try {
      const response = await verifyotp(body);
      if (response.data.status === 200) {
        toast.success(response.data.message);
        clearPendingSignup();
        localStorage.setItem("isVerified", true);
        await fetchProfile();
        setIsVerifying(false);
        window.dataLayer.push({ event: "email_verified", method: "email" });
        window.dataLayer.push({ event: "sign_up", method: "email" });

        // Function to check if userInfo is set and navigate accordingly.
        const checkAndNavigate = () => {
          if (localStorage.getItem("userInfo") !== null) {
            // Retrieve gameurl from local storage
            let savedUrl = JSON.parse(localStorage.getItem("gameurl"));

            // Replace "contests" with "contest" in the saved URL if it exists
            if (savedUrl && savedUrl.includes("/contests/")) {
              savedUrl = savedUrl.replace("/contests/", "/contest/");
            }

            // Redirect to the saved URL if it exists, otherwise go to /profile
            if (savedUrl) {
              window.location.href = savedUrl;
            } else {
              navigate("/profile");
            }
          } else {
            // If userInfo is not yet available, check again after some time.
            setTimeout(checkAndNavigate, 500);
          }
        };

        checkAndNavigate();
      } else {
        setIsVerifying(false);
        setCodeError(response.data.message);
        window.dataLayer.push({
          event: "email_verification_failed",
          verification_failure_reason: verificationFailureReason(response.data.message),
        });
      }
    } catch (error) {
      setIsVerifying(false);
      setCodeError(
        error?.response?.data?.message ||
          "We could not verify that code. Please try again.",
      );
      window.dataLayer.push({
        event: "email_verification_failed",
        verification_failure_reason: "system_error",
      });
    }
  };

  // One action, replacing the old "Resend" and "Send to Email" pair - which
  // called the same function anyway, and both failed because the screen looked
  // for the address under a key only sign-in ever wrote.
  const resendCode = async () => {
    if (isResending || isVerifying) return;

    const email = pendingEmail || getPendingSignupEmail();
    if (!email) {
      toast.error("We could not find the email for this signup. Start again.");
      return;
    }

    setIsResending(true);
    try {
      const response = await sendotp({ email, otp_purpose: 2 });

      if (response.data.status === 200) {
        const confirmedEmail = response.data.data?.email || email;
        setPendingEmail(confirmedEmail);
        setVerificationToken(response.data.data?.email_token);
        setCodeError(null);
        setVerificationCode("");
        toast.success(`A new code was sent to ${confirmedEmail}.`);
        window.dataLayer.push({ event: "email_verification_resent", method: "email" });
      } else {
        // Rate limiting lives on the backend; this is where its
        // "Please wait a moment before requesting another code." surfaces.
        toast.error(resendCodeMessage(response.data.message));
      }
    } catch (error) {
      toast.error(
        resendCodeMessage(
          error?.response?.data?.message,
          "Could not send a new code. Try again.",
        ),
      );
    } finally {
      setIsResending(false);
    }
  };

  const changeEmail = () => {
    clearPendingSignup();
    navigate("/auth/register");
  };

  // Accept a typed or pasted code. Pasting from an email client routinely drags
  // in spaces or a newline, so anything that is not a digit is dropped rather
  // than rejected.
  const handleCodeChange = (value) => {
    setVerificationCode(value.replace(/\D/g, "").slice(0, CODE_LENGTH));
    if (codeError) setCodeError(null);
  };

  const busy = isVerifying || isResending;

  const form = (
    <form onSubmit={handleVerification} className="w-full max-w-sm">
      <h1 className="text-primaryColor font-bold text-4xl mb-4 font-montserrat">
        Verify your email
      </h1>
      <p className="text-gray-600 mb-8 font-montserrat text-sm leading-relaxed">
        We sent an {CODE_LENGTH} digit code to{" "}
        <span className="font-semibold text-primaryColor break-all">
          {pendingEmail || "your email address"}
        </span>
        . Enter it below within {CODE_MINUTES} minutes to finish creating your
        account.
      </p>

      <label
        htmlFor="verification-code"
        className="block text-gray-500 text-xs tracking-wide mb-2 font-montserrat"
      >
        VERIFICATION CODE
      </label>
      <input
        id="verification-code"
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        maxLength={CODE_LENGTH}
        placeholder="Enter code"
        value={verificationCode}
        onChange={(e) => handleCodeChange(e.target.value)}
        aria-invalid={codeError ? "true" : undefined}
        className="w-full rounded-full bg-gray-200 placeholder-gray-500 text-gray-700 px-5 py-4 tracking-widest focus:outline-none"
      />
      {codeError ? (
        <span role="alert" className="block text-red-500 text-xs mt-2 ml-2">
          {codeError}
        </span>
      ) : null}

      <button
        type="submit"
        disabled={busy}
        className="w-full bg-primaryColor text-white rounded-2xl py-4 mt-6 font-bold text-lg focus:outline-none active:opacity-80 transition disabled:opacity-60"
      >
        {isVerifying ? "Verifying" : "Verify Account"}
      </button>

      <div className="text-center mt-6 space-y-2">
        <p className="text-gray-600 text-sm font-montserrat">
          Didn&apos;t receive the email?{" "}
          <button
            type="button"
            onClick={resendCode}
            disabled={busy}
            className="text-primaryColor font-bold underline focus:outline-none disabled:opacity-60"
          >
            {isResending ? "Sending" : "Resend Code"}
          </button>
        </p>
        <p className="text-gray-600 text-sm font-montserrat">
          Wrong email?{" "}
          <button
            type="button"
            onClick={changeEmail}
            disabled={busy}
            className="text-primaryColor font-bold underline focus:outline-none disabled:opacity-60"
          >
            Change Email
          </button>
        </p>
      </div>
    </form>
  );

  // Mobile: full width, with the small QUSHi icon above the form.
  if (isMobileView) {
    return (
      <PageContainer>
        <div
          className="min-h-screen w-full flex flex-col items-center justify-center px-8 py-12"
          style={{ backgroundColor: "#F2F0EC" }}
        >
          <img
            src={"/qushi_logo.png"}
            alt="QUSHi"
            className="w-20 mx-auto mb-8"
          />
          {form}
        </div>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      {/* Desktop: the same split screen the account-creation screens use, so
          verification does not read as a different product mid-signup. */}
      <div className="flex min-h-screen">
        <div className="w-1/2 bg-primaryColor flex flex-col items-center justify-center">
          <img src="/Logo.png" alt="QUSHi" className="w-64" />
          <p
            className="text-white text-sm mt-4 uppercase whitespace-nowrap"
            style={{ letterSpacing: "0.3em", marginRight: "-0.3em" }}
          >
            TRADE TRACK PROVE IT
          </p>
        </div>
        <div
          className="w-1/2 flex items-center justify-center px-12 py-12 min-h-screen"
          style={{ backgroundColor: "#F2F0EC" }}
        >
          {form}
        </div>
      </div>
    </PageContainer>
  );
};

export default VerificationPage;
